import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLeaguePrefix } from '../../utils/leagueLinks';
import { useActiveLeague } from '../../hooks/useActiveLeague';
import { useDrag } from 'react-dnd';
import { ItemTypes } from './itemTypes.js';

const PlayerListItem = React.memo(({ 
  player, 
  playersOnField,
  playerIdsOnBench,
  removePlayer, 
  touchPlayer, 
  touchedPlayer 
}) => {
  
  // Find the full slot info if the player is on the field
  const slotOnField = playersOnField.find(slot => slot.player_id === player.wyId);
  const isOnField = !!slotOnField;
  const isOnBench = playerIdsOnBench.includes(player.wyId);
  const isUnavailable = isOnField || isOnBench;

  // --- Hooks called at top level ---
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.PLAYER,
    item: { type: ItemTypes.PLAYER, playerInfo: player },
    canDrag: true, // Allow dragging for all players in the list
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }), [player]);

  useEffect(() => {
    // close dropdown if click outside
    function close(e) {
      touchPlayer(null);
    }
    // add or remove event listener
    if (touchedPlayer && touchedPlayer.id === player.id) {
      window.addEventListener("click", close);
    }
    // cleanup
    return function removeListener() {
      window.removeEventListener("click", close);
    }
  }, [touchedPlayer, player.id]); // Dependencies for the effect
  // --- End Hooks ---

  const handleClickButton = (event, id) => {
    event.stopPropagation();
    removePlayer(id);
  };

  const handleTouch = (event, obj) => {
    event.stopPropagation();
    touchPlayer(obj);
  };

  // Determine active/touched state
  const isTouched = touchedPlayer && touchedPlayer.wyId === player.wyId;

  // Simplified class logic as requested
  const activeClass = (isOnField || isOnBench) ? " active " : "";
  const subtClass = isOnBench ? " subt " : "";
  const touchedClass = isTouched ? " touched " : "";

  const prefix = useLeaguePrefix();
  const { activeLeague } = useActiveLeague();

  return (
    <div 
      ref={drag} // Apply drag ref here
      key={player.id || player.wyId} 
      className={`player-list-item${activeClass}${subtClass}${touchedClass}`}
      onClick={(event) => { handleTouch(event, player) }}
    >
      <div className="type">
        {/* Display position from field, "SUB" for bench, or player's default position */}
        {isOnField ? slotOnField.title : (isOnBench ? 'SUB' : player.currentPosition)}
      </div>

      <div className="name">
        <div className="name-val">
          {player.name}
        </div>
        {/* Conditionally render clear button only if player is on the field */}
        {isOnField && (
          <button 
            type="button" 
            className="pp-clear-btn" 
            aria-label="Clear player"  
            onClick={(event) => { handleClickButton(event, player.wyId) }}
          />
        )}
      </div>

      <div className="score">
        {player.rating ?? player.overall ?? '-'}
      </div>
      <Link to={`${prefix}/player/${player.wyId}${activeLeague?._id ? `?leagueId=${activeLeague._id}` : ''}`} className="ico icon-tip" />
    </div>
  );
});

export default PlayerListItem;
